import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/context/AuthContext';
import { 
  Sparkles, Building2, Car, Watch, TrendingUp, Trophy, 
  ChevronRight, Home, Wallet, Target, Crown, Gem, 
  ArrowLeft, RefreshCw, Heart, Zap, LogOut, Settings, MessageCircle, ExternalLink
} from 'lucide-react';
import axios from 'axios';
import Nav from '@/components/Nav';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Budget allocation strategies
const ALLOCATION_OPTIONS = [
  {
    id: 'luxury_first',
    name: 'Luxury First',
    icon: Crown,
    description: 'Prioritize premium lifestyle assets',
    color: 'from-purple-500 to-pink-500',
    allocation: { property: 70, car: 25, experience: 5 }
  },
  {
    id: 'balanced',
    name: 'Balanced',
    icon: Target,
    description: 'Equal focus on lifestyle and value',
    color: 'from-blue-500 to-cyan-500',
    allocation: { property: 60, car: 30, experience: 10 }
  },
  {
    id: 'roi_focused',
    name: 'ROI Focused',
    icon: TrendingUp,
    description: 'Maximize investment returns',
    color: 'from-emerald-500 to-green-500',
    allocation: { property: 80, car: 15, experience: 5 }
  }
];

// Dubai locations
const LOCATIONS = [
  'Palm Jumeirah', 'Downtown Dubai', 'Dubai Marina', 'Business Bay',
  'Jumeirah Beach', 'DIFC', 'Emirates Hills', 'Dubai Hills',
  'Bluewaters Island', 'Dubai Creek Harbour'
];

const PortfolioPreviewPage = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { user, logout } = useAuth();
  
  // Form state
  const [budget, setBudget] = useState(10000000);
  const [priority, setPriority] = useState('balanced');
  const [selectedLocations, setSelectedLocations] = useState([]);
  
  // Result state
  const [portfolio, setPortfolio] = useState(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: Input, 2: Result
  
  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/');
  };
  
  const formatCurrency = (value) => {
    if (value >= 1000000) {
      return `AED ${(value / 1000000).toFixed(1)}M`;
    }
    return `AED ${value.toLocaleString()}`;
  };

  const handleLocationToggle = (location) => {
    if (selectedLocations.includes(location)) {
      setSelectedLocations(selectedLocations.filter(l => l !== location));
    } else if (selectedLocations.length < 3) {
      setSelectedLocations([...selectedLocations, location]);
    }
  };

  const generatePortfolio = async () => {
    setLoading(true);
    try {
      
      const response = await axios.post(`${API}/portfolio/preview`, {
        budget,
        priority,
        location_preference: selectedLocations.length > 0 ? selectedLocations : ['Dubai']
      }, {
        withCredentials: true
      });
      
      setPortfolio(response.data);
      setStep(2);
      toast.success('Portfolio generated successfully!');
    } catch (error) {
      toast.error('Failed to generate portfolio');
    } finally {
      setLoading(false);
    }
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case 'Platinum': return 'from-slate-300 to-slate-500';
      case 'Gold': return 'from-yellow-400 to-amber-600';
      case 'Silver': return 'from-gray-300 to-gray-500';
      default: return 'from-gray-400 to-gray-600';
    }
  };

  const getTierBadgeColor = (tier) => {
    switch (tier) {
      case 'Platinum': return 'bg-gradient-to-r from-slate-400 to-slate-600 text-white';
      case 'Gold': return 'bg-gradient-to-r from-yellow-500 to-amber-600 text-black';
      case 'Silver': return 'bg-gradient-to-r from-gray-400 to-gray-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  return (
    <div className={`min-h-screen ${theme === 'light' ? 'bg-gray-50' : 'bg-obsidian'}`}>
      {/* Navigation */}
      <Nav activePage="/portfolio" tier="premium" />
      
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold/10 border border-gold/20 mb-6">
            <Sparkles className="w-4 h-4 text-gold" />
            <span className="text-gold text-sm font-medium tracking-wider uppercase">AI Portfolio Preview</span>
            <span className="text-xs px-2 py-0.5 bg-gold/20 rounded-full text-gold">Beta</span>
          </div>
          <h1 className="font-playfair text-4xl sm:text-5xl font-bold mb-4">
            Design Your <span className="text-gold">Dream Portfolio</span>
          </h1>
          <p className={`text-lg max-w-2xl mx-auto ${theme === 'light' ? 'text-gray-600' : 'text-text-secondary'}`}>
            Our AI analyzes your preferences to curate a personalized luxury lifestyle portfolio
          </p>
        </motion.div>

        <AnimatePresence mode="wait">
          {step === 1 ? (
            /* Input Form */
            <motion.div
              key="input"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="space-y-8"
            >
              {/* Budget Section */}
              <div className={`p-8 rounded-2xl ${theme === 'light' ? 'bg-white border border-gray-200' : 'luxury-card'}`}>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-gold/10 flex items-center justify-center">
                    <Wallet className="w-6 h-6 text-gold" />
                  </div>
                  <div>
                    <h2 className="font-playfair text-2xl font-semibold">Total Budget</h2>
                    <p className={`text-sm ${theme === 'light' ? 'text-gray-500' : 'text-text-secondary'}`}>
                      Set your investment capacity
                    </p>
                  </div>
                </div>
                
                <div className="text-center mb-8">
                  <div className="text-5xl font-bold text-gold mb-2">
                    {formatCurrency(budget)}
                  </div>
                  <p className={`text-sm ${theme === 'light' ? 'text-gray-500' : 'text-text-secondary'}`}>
                    {budget >= 50000000 ? 'Ultra High Net Worth' : budget >= 20000000 ? 'High Net Worth' : 'Affluent'}
                  </p>
                </div>
                
                <Slider
                  value={[budget]}
                  onValueChange={(value) => setBudget(value[0])}
                  min={1000000}
                  max={100000000}
                  step={500000}
                  className="mb-4"
                  data-testid="budget-slider"
                />
                
                <div className="flex justify-between text-sm text-text-secondary">
                  <span>AED 1M</span>
                  <span>AED 100M</span>
                </div>
              </div>

              {/* Priority Strategy */}
              <div className={`p-8 rounded-2xl ${theme === 'light' ? 'bg-white border border-gray-200' : 'luxury-card'}`}>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-gold/10 flex items-center justify-center">
                    <Target className="w-6 h-6 text-gold" />
                  </div>
                  <div>
                    <h2 className="font-playfair text-2xl font-semibold">Investment Strategy</h2>
                    <p className={`text-sm ${theme === 'light' ? 'text-gray-500' : 'text-text-secondary'}`}>
                      Choose your portfolio allocation approach
                    </p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {ALLOCATION_OPTIONS.map((option) => {
                    const Icon = option.icon;
                    const isSelected = priority === option.id;
                    return (
                      <motion.button
                        key={option.id}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setPriority(option.id)}
                        className={`relative p-6 rounded-xl text-left transition-all ${
                          isSelected 
                            ? 'bg-gold/10 border-2 border-gold' 
                            : theme === 'light' 
                              ? 'bg-gray-50 border-2 border-transparent hover:border-gray-200' 
                              : 'bg-white/5 border-2 border-transparent hover:border-white/10'
                        }`}
                        data-testid={`strategy-${option.id}`}
                      >
                        {isSelected && (
                          <div className="absolute top-3 right-3 w-6 h-6 rounded-full bg-gold flex items-center justify-center">
                            <Zap className="w-4 h-4 text-black" />
                          </div>
                        )}
                        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${option.color} flex items-center justify-center mb-4`}>
                          <Icon className="w-6 h-6 text-white" />
                        </div>
                        <h3 className="font-semibold text-lg mb-1">{option.name}</h3>
                        <p className={`text-sm mb-4 ${theme === 'light' ? 'text-gray-500' : 'text-text-secondary'}`}>
                          {option.description}
                        </p>
                        <div className="space-y-1 text-xs">
                          <div className="flex justify-between">
                            <span className="text-text-secondary">Property</span>
                            <span className="font-medium">{option.allocation.property}%</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-text-secondary">Automotive</span>
                            <span className="font-medium">{option.allocation.car}%</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-text-secondary">Experience</span>
                            <span className="font-medium">{option.allocation.experience}%</span>
                          </div>
                        </div>
                      </motion.button>
                    );
                  })}
                </div>
              </div>

              {/* Location Preference */}
              <div className={`p-8 rounded-2xl ${theme === 'light' ? 'bg-white border border-gray-200' : 'luxury-card'}`}>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-gold/10 flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-gold" />
                  </div>
                  <div>
                    <h2 className="font-playfair text-2xl font-semibold">Preferred Locations</h2>
                    <p className={`text-sm ${theme === 'light' ? 'text-gray-500' : 'text-text-secondary'}`}>
                      Select up to 3 areas (optional)
                    </p>
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-2">
                  {LOCATIONS.map((location) => {
                    const isSelected = selectedLocations.includes(location);
                    return (
                      <button
                        key={location}
                        onClick={() => handleLocationToggle(location)}
                        className={`px-4 py-2 rounded-full text-sm transition-all ${
                          isSelected
                            ? 'bg-gold text-black font-medium'
                            : theme === 'light'
                              ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              : 'bg-white/10 text-white hover:bg-white/20'
                        }`}
                        data-testid={`location-${location.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {location}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Generate Button */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="flex justify-center"
              >
                <Button
                  onClick={generatePortfolio}
                  disabled={loading}
                  className="bg-gold hover:bg-gold-light text-black px-12 py-6 text-lg font-semibold rounded-xl"
                  data-testid="generate-portfolio-btn"
                >
                  {loading ? (
                    <>
                      <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5 mr-2" />
                      Generate My Portfolio
                    </>
                  )}
                </Button>
              </motion.div>
            </motion.div>
          ) : (
            /* Results */
            <motion.div
              key="result"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-8"
            >
              {/* Back Button */}
              <Button
                variant="ghost"
                onClick={() => setStep(1)}
                className="mb-4"
                data-testid="back-to-input-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Modify Preferences
              </Button>

              {/* Portfolio Summary Card */}
              {portfolio && (
                <>
                  <div className={`p-8 rounded-2xl ${theme === 'light' ? 'bg-white border border-gray-200' : 'luxury-card'}`}>
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-gold to-amber-600 flex items-center justify-center">
                          <Trophy className="w-6 h-6 text-black" />
                        </div>
                        <div>
                          <h2 className="font-playfair text-2xl font-semibold">Your Dream Portfolio</h2>
                          <p className={`text-sm ${theme === 'light' ? 'text-gray-500' : 'text-text-secondary'}`}>
                            AI-curated based on your preferences
                          </p>
                        </div>
                      </div>
                      <div className={`px-4 py-2 rounded-full ${getTierBadgeColor(portfolio.portfolio_prestige_score >= 85 ? 'Platinum' : portfolio.portfolio_prestige_score >= 70 ? 'Gold' : 'Silver')}`}>
                        <span className="font-semibold">
                          {portfolio.portfolio_prestige_score >= 85 ? 'Platinum' : portfolio.portfolio_prestige_score >= 70 ? 'Gold' : 'Silver'} Portfolio
                        </span>
                      </div>
                    </div>
                    
                    {/* Summary Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                      <div className={`p-4 rounded-xl ${theme === 'light' ? 'bg-gray-50' : 'bg-white/5'}`}>
                        <p className="text-sm text-text-secondary mb-1">Total Budget</p>
                        <p className="text-xl font-bold text-gold">{formatCurrency(portfolio.budget)}</p>
                      </div>
                      <div className={`p-4 rounded-xl ${theme === 'light' ? 'bg-gray-50' : 'bg-white/5'}`}>
                        <p className="text-sm text-text-secondary mb-1">Budget Used</p>
                        <p className="text-xl font-bold">{formatCurrency(portfolio.total_budget_used)}</p>
                      </div>
                      <div className={`p-4 rounded-xl ${theme === 'light' ? 'bg-gray-50' : 'bg-white/5'}`}>
                        <p className="text-sm text-text-secondary mb-1">Estimated ROI</p>
                        <p className="text-xl font-bold text-emerald-500">{portfolio.estimated_roi}% p.a.</p>
                      </div>
                      <div className={`p-4 rounded-xl ${theme === 'light' ? 'bg-gray-50' : 'bg-white/5'}`}>
                        <p className="text-sm text-text-secondary mb-1">Prestige Score</p>
                        <p className="text-xl font-bold text-gold">{portfolio.portfolio_prestige_score}/100</p>
                      </div>
                    </div>
                    
                    {/* Disclaimer */}
                    <p className={`text-xs ${theme === 'light' ? 'text-gray-400' : 'text-text-muted'} italic`}>
                      AI portfolio suggestions are indicative and non-contractual. All recommendations comply with Dubai Land Department guidelines.
                    </p>
                  </div>

                  {/* Selected Assets */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* No assets found message */}
                    {!portfolio.selected_property && !portfolio.selected_car && !portfolio.selected_experience && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="col-span-3 text-center py-12 glass-card rounded-2xl"
                      >
                        <div className="w-16 h-16 rounded-full bg-gold/10 flex items-center justify-center mx-auto mb-4">
                          <Building2 className="w-8 h-8 text-gold" />
                        </div>
                        <h3 className="font-playfair text-xl font-semibold mb-2">No Matching Assets Found</h3>
                        <p className="text-text-secondary mb-4">
                          Try adjusting your budget or location preferences to see more options.
                        </p>
                        <Button
                          onClick={() => setStep(1)}
                          variant="outline"
                          className="border-gold text-gold hover:bg-gold hover:text-black"
                        >
                          Modify Preferences
                        </Button>
                      </motion.div>
                    )}

                    {/* Property */}
                    {portfolio.selected_property && (
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        whileHover={{ scale: 1.02, y: -4 }}
                        onClick={() => navigate(`/properties/${portfolio.selected_property.asset_id}`)}
                        className={`rounded-2xl overflow-hidden cursor-pointer group ${theme === 'light' ? 'bg-white border border-gray-200 hover:border-gold' : 'luxury-card'}`}
                        data-testid="portfolio-property-card"
                      >
                        <div className="relative aspect-[4/3] overflow-hidden">
                          <img
                            src={portfolio.selected_property.image || 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=600'}
                            alt={portfolio.selected_property.title}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                          />
                          <div className="absolute top-4 left-4 flex gap-2">
                            <span className="px-3 py-1 rounded-full bg-gold/90 text-black text-xs font-bold uppercase">
                              Property
                            </span>
                            {portfolio.selected_property.asset_tier && (
                              <span className={`px-3 py-1 rounded-full text-xs font-bold ${getTierBadgeColor(portfolio.selected_property.asset_tier)}`}>
                                {portfolio.selected_property.asset_tier}
                              </span>
                            )}
                          </div>
                          {/* View overlay */}
                          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <span className="text-white font-semibold flex items-center gap-2">
                              <ChevronRight className="w-5 h-5" /> View Property
                            </span>
                          </div>
                        </div>
                        <div className="p-6">
                          <h3 className="font-playfair text-xl font-semibold mb-2 group-hover:text-gold transition-colors">{portfolio.selected_property.title}</h3>
                          <div className="flex items-center justify-between mb-4">
                            <span className="text-2xl font-bold text-gold">{formatCurrency(portfolio.selected_property.price)}</span>
                            <span className={`text-sm ${theme === 'light' ? 'text-gray-500' : 'text-text-secondary'}`}>
                              {portfolio.property_allocation}% allocation
                            </span>
                          </div>
                          {portfolio.selected_property.dreamlife_score && (
                            <div className="flex items-center gap-2 text-sm">
                              <Gem className="w-4 h-4 text-gold" />
                              <span>ASTIIV BASGAT Score: {portfolio.selected_property.dreamlife_score}</span>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    )}

                    {/* Car */}
                    {portfolio.selected_car && (
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        whileHover={{ scale: 1.02, y: -4 }}
                        onClick={() => navigate(`/marketplace/${portfolio.selected_car.asset_id}`)}
                        className={`rounded-2xl overflow-hidden cursor-pointer group ${theme === 'light' ? 'bg-white border border-gray-200 hover:border-gold' : 'luxury-card'}`}
                        data-testid="portfolio-car-card"
                      >
                        <div className="relative aspect-[4/3] overflow-hidden">
                          <img
                            src={portfolio.selected_car.image || 'https://images.unsplash.com/photo-1636546055867-0cc0d3fed34d?w=600'}
                            alt={portfolio.selected_car.title}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                          />
                          <div className="absolute top-4 left-4 flex gap-2">
                            <span className="px-3 py-1 rounded-full bg-blue-500/90 text-white text-xs font-bold uppercase">
                              Automotive
                            </span>
                            {portfolio.selected_car.asset_tier && (
                              <span className={`px-3 py-1 rounded-full text-xs font-bold ${getTierBadgeColor(portfolio.selected_car.asset_tier)}`}>
                                {portfolio.selected_car.asset_tier}
                              </span>
                            )}
                          </div>
                          {/* View overlay */}
                          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <span className="text-white font-semibold flex items-center gap-2">
                              <ChevronRight className="w-5 h-5" /> View Car
                            </span>
                          </div>
                        </div>
                        <div className="p-6">
                          <h3 className="font-playfair text-xl font-semibold mb-2 group-hover:text-gold transition-colors">{portfolio.selected_car.title}</h3>
                          <div className="flex items-center justify-between mb-4">
                            <span className="text-2xl font-bold text-gold">{formatCurrency(portfolio.selected_car.price)}</span>
                            <span className={`text-sm ${theme === 'light' ? 'text-gray-500' : 'text-text-secondary'}`}>
                              {portfolio.car_allocation}% allocation
                            </span>
                          </div>
                          {portfolio.selected_car.dreamlife_score && (
                            <div className="flex items-center gap-2 text-sm">
                              <Gem className="w-4 h-4 text-gold" />
                              <span>ASTIIV BASGAT Score: {portfolio.selected_car.dreamlife_score}</span>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    )}

                    {/* Experience */}
                    {portfolio.selected_experience && (
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                        whileHover={{ scale: 1.02, y: -4 }}
                        onClick={() => navigate(`/marketplace/${portfolio.selected_experience.asset_id}`)}
                        className={`rounded-2xl overflow-hidden cursor-pointer group ${theme === 'light' ? 'bg-white border border-gray-200 hover:border-gold' : 'luxury-card'}`}
                        data-testid="portfolio-experience-card"
                      >
                        <div className="relative aspect-[4/3] overflow-hidden">
                          <img
                            src={portfolio.selected_experience.image || 'https://images.unsplash.com/photo-1697016938269-f9d10c14024e?w=600'}
                            alt={portfolio.selected_experience.title}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                          />
                          <div className="absolute top-4 left-4 flex gap-2">
                            <span className="px-3 py-1 rounded-full bg-purple-500/90 text-white text-xs font-bold uppercase">
                              Experience
                            </span>
                            {portfolio.selected_experience.asset_tier && (
                              <span className={`px-3 py-1 rounded-full text-xs font-bold ${getTierBadgeColor(portfolio.selected_experience.asset_tier)}`}>
                                {portfolio.selected_experience.asset_tier}
                              </span>
                            )}
                          </div>
                          {/* View overlay */}
                          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <span className="text-white font-semibold flex items-center gap-2">
                              <ChevronRight className="w-5 h-5" /> View Details
                            </span>
                          </div>
                        </div>
                        <div className="p-6">
                          <h3 className="font-playfair text-xl font-semibold mb-2 group-hover:text-gold transition-colors">{portfolio.selected_experience.title}</h3>
                          <div className="flex items-center justify-between mb-4">
                            <span className="text-2xl font-bold text-gold">{formatCurrency(portfolio.selected_experience.price)}</span>
                            <span className={`text-sm ${theme === 'light' ? 'text-gray-500' : 'text-text-secondary'}`}>
                              {portfolio.experience_allocation}% allocation
                            </span>
                          </div>
                          {portfolio.selected_experience.dreamlife_score && (
                            <div className="flex items-center gap-2 text-sm">
                              <Gem className="w-4 h-4 text-gold" />
                              <span>ASTIIV BASGAT Score: {portfolio.selected_experience.dreamlife_score}</span>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </div>

                  {/* CTA */}
                  <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
                    <Button
                      onClick={() => setStep(1)}
                      variant="outline"
                      className="border-gold text-gold hover:bg-gold hover:text-black"
                      data-testid="regenerate-btn"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Generate New Portfolio
                    </Button>
                    <Button
                      onClick={() => navigate('/concierge')}
                      className="bg-gold hover:bg-gold-light text-black"
                      data-testid="talk-to-concierge-btn"
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      Talk to Concierge
                    </Button>
                  </div>
                </>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
};

export default PortfolioPreviewPage;
